import { BellOutlined, MenuOutlined, SearchOutlined } from "@ant-design/icons";
import { Avatar, Button, Input, Layout } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLogout } from "@/modules/auth/hooks/useAuth";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { LogoutDoorIcon } from "@/shared/components/icons/LogoutDoorIcon";
import {
	type AppRole,
	getActiveNavItem,
	getRouteAccess,
	getVisibleGroups,
	roleOptions,
} from "@/shared/config/navigation";

const { Sider, Content } = Layout;

function AccessDeniedPage({
	allowedRoles,
	currentRole,
}: {
	allowedRoles: AppRole[];
	currentRole: AppRole;
}) {
	const currentLabel = roleOptions.find((item) => item.value === currentRole)?.label ?? currentRole;
	const allowedLabels = allowedRoles
		.map((role) => roleOptions.find((item) => item.value === role)?.label ?? role)
		.join(", ");

	return (
		<div className="crm-card pad empty">
			Bu sahifa <strong>{currentLabel}</strong> roliga ko'rinmaydi.
			<br />
			Ko'rish uchun: {allowedLabels}.
		</div>
	);
}

function determineActiveNavItem(pathname: string, fromDashboard: boolean, returnTo?: string) {
	if (fromDashboard) {
		return getActiveNavItem("/dashboard");
	}
	const isSalesOrContract = pathname.startsWith("/sales/") || pathname.startsWith("/contract");
	const isClientSalePayOrContract =
		pathname.startsWith("/clients/") || pathname.startsWith("/payments/") || isSalesOrContract;

	if (returnTo === "/payments" && isSalesOrContract) {
		return getActiveNavItem("/payments");
	}
	if (returnTo === "/reports/debts" && isClientSalePayOrContract) {
		return getActiveNavItem("/reports/debts");
	}
	if (returnTo === "/reports/payments" && isClientSalePayOrContract) {
		return getActiveNavItem("/reports/payments");
	}
	if (returnTo === "/reports/rooms" && pathname.startsWith("/rooms")) {
		return getActiveNavItem("/reports/rooms");
	}
	return getActiveNavItem(pathname);
}

function isActivePath(
	pathname: string,
	itemPath: string,
	fromDashboard?: boolean,
	search?: string,
	returnTo?: string
) {
	const query = new URLSearchParams(search ?? "");
	if (query.has("saleId") || query.has("clientId")) {
		const targetPath = query.has("saleId") ? "/sales" : "/clients";
		if (itemPath === targetPath) {
			return pathname.startsWith("/payments/new") || pathname.startsWith("/contract");
		}
		if (itemPath === "/payments" || itemPath === "/contract") {
			return false;
		}
	}

	const activeNavItem = determineActiveNavItem(pathname, !!fromDashboard, returnTo);
	if (activeNavItem) {
		return activeNavItem.path === itemPath;
	}

	return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

export function MainLayout() {
	const [mobileOpen, setMobileOpen] = useState(false);
	const { user, isAuthenticated, isLoading } = useAuthStore();
	const { mutate: logoutMutate } = useLogout();
	const navigate = useNavigate();

	useEffect(() => {
		if (!(isLoading || isAuthenticated)) {
			navigate("/login", { replace: true });
		}
	}, [isLoading, isAuthenticated, navigate]);

	const role = user?.role ?? "manager";

	const location = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
		const stage = document.querySelector(".crm-stage");
		if (stage) {
			stage.scrollTo(0, 0);
		}
		const main = document.querySelector(".crm-main");
		if (main) {
			main.scrollTo(0, 0);
		}
	}, []);

	const state = location.state as { fromDashboard?: boolean; returnTo?: string } | undefined;
	const fromDashboard = state?.fromDashboard === true;
	const returnTo = state?.returnTo;
	const activeNavItem = determineActiveNavItem(location.pathname, fromDashboard, returnTo);
	const routeAccess = getRouteAccess(location.pathname);
	const groups = useMemo(() => getVisibleGroups(role), [role]);
	const roleLabel = roleOptions.find((item) => item.value === role)?.label ?? role;
	const canViewActivePage = !routeAccess.roles || routeAccess.roles.includes(role);

	const initials = useMemo(() => {
		if (!user) {
			return "?";
		}
		if (user.fullName) {
			const parts = user.fullName.trim().split(/\s+/).filter(Boolean);
			if (parts.length >= 2) {
				return (parts[0][0] + parts[1][0]).toUpperCase();
			}
			return (parts[0] || "?").substring(0, 2).toUpperCase();
		}
		return (user.username?.trim() || "User").substring(0, 2).toUpperCase();
	}, [user]);

	const userName = user?.fullName?.trim() || user?.username || "Mehmon";

	const go = (path: string) => {
		navigate(path);
		setMobileOpen(false);
	};

	const handleLogout = (e: React.MouseEvent) => {
		e.stopPropagation();
		logoutMutate();
	};

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Avatar size="large" />
			</div>
		);
	}

	return (
		<Layout className="crm-shell">
			<Sider width={248} className={`crm-sidebar ${mobileOpen ? "open" : ""}`} trigger={null}>
				<div className="crm-brand">
					<div className="crm-logo">Q</div>
					<div>
						<div className="crm-brand-title">QurilishCRM</div>
						<div className="crm-brand-subtitle">Unicorp Group</div>
					</div>
				</div>

				<nav className="crm-nav">
					{groups.map((group) => (
						<div className="crm-nav-group" key={group.title}>
							<div className="crm-nav-label">{group.title}</div>
							{group.items.map((item) => (
								<button
									className={`crm-nav-item ${
										isActivePath(
											location.pathname,
											item.path,
											fromDashboard,
											location.search,
											returnTo
										)
											? "active"
											: ""
									}`}
									key={item.path}
									onClick={() => go(item.path)}
									type="button"
								>
									<span className="crm-nav-icon">{item.icon}</span>
									<span>{item.label}</span>
									{item.badge ? <span className="crm-nav-badge">{item.badge}</span> : null}
								</button>
							))}
						</div>
					))}
				</nav>

				<div className="crm-side-foot">
					<div
						className="crm-side-foot-row"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<button
							className="crm-side-user"
							onClick={() => go("/profile")}
							type="button"
							style={{ flex: 1, minWidth: 0 }}
						>
							<Avatar size={28} className="crm-avatar">
								{initials}
							</Avatar>
							<span className="crm-user-copy">
								<span
									className="crm-user-name"
									style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
								>
									{userName}
								</span>
								<span className="crm-user-role">{roleLabel}</span>
							</span>
						</button>
						<button
							onClick={handleLogout}
							type="button"
							className="crm-logout-btn"
							title="Chiqish"
							aria-label="Chiqish"
						>
							<LogoutDoorIcon />
						</button>
					</div>
				</div>
			</Sider>

			{mobileOpen ? (
				<button className="crm-scrim" onClick={() => setMobileOpen(false)} type="button" />
			) : null}

			<Layout className="crm-main">
				<header className="crm-topbar">
					<Button
						className="crm-menu-btn"
						icon={<MenuOutlined />}
						onClick={() => setMobileOpen(true)}
					/>
					<div>
						<div className="crm-page-title">{activeNavItem.label}</div>
						<div className="crm-page-subtitle">{activeNavItem.subtitle}</div>
					</div>

					<div className="crm-top-actions">
						<Input
							className="crm-search"
							prefix={<SearchOutlined />}
							placeholder="Mijoz, xona, sotuv qidirish..."
						/>
						<Button className="crm-bell" icon={<BellOutlined />}>
							<span className="crm-dot" />
						</Button>
					</div>
				</header>

				<Content className="crm-stage">
					{canViewActivePage ? (
						<Outlet context={{ role }} />
					) : (
						<AccessDeniedPage allowedRoles={routeAccess.roles ?? []} currentRole={role} />
					)}
				</Content>
			</Layout>
		</Layout>
	);
}
